<script setup lang="ts">
/* One item in the "Back with you" panel: what came back, who sent it, their
   words, what moves with it, and the way into it. A thread card also replies
   in place for anyone who can write, so answering a tag doesn't mean leaving
   the page you're on; the panel decides which card is replying. */
import type { BackWithYouItem } from '~/utils/policy-studio/back-with-you'

defineProps<{ item: BackWithYouItem }>()

const replying = defineModel<boolean>('replying', { default: false })
const canWrite = useCanWriteContent()
</script>

<template>
  <!-- shrink-0: the sidebar body is a flex column, which otherwise squeezes each card to fit. -->
  <UCard class="border-warning/40 shrink-0" :ui="{ body: 'p-3 sm:p-3' }">
    <NuxtLink :to="item.to" class="group block">
      <div class="flex items-center gap-2">
        <span
          v-if="item.unread"
          class="bg-primary size-2 shrink-0 rounded-full"
          aria-label="Unread"
        />
        <span class="text-primary truncate font-mono text-xs font-semibold">
          {{ item.id }}
        </span>
        <span class="text-muted ml-auto shrink-0 text-xs">{{ item.when }}</span>
      </div>

      <p class="text-default mt-1 line-clamp-2 text-sm group-hover:underline">
        {{ item.title }}
      </p>
    </NuxtLink>

    <div class="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <UBadge
        class="max-w-full"
        color="warning"
        :label="item.from"
        size="sm"
        :title="item.from"
        variant="subtle"
      />
      <span v-if="item.context" class="text-dimmed truncate text-xs">
        {{ item.context }}
      </span>
    </div>

    <div
      class="border-default bg-elevated/50 mt-2 flex items-start gap-2 rounded-md border p-2.5"
    >
      <UIcon name="i-ph-quotes" class="text-muted mt-0.5 size-3.5 shrink-0" />
      <p class="text-muted min-w-0 flex-1 text-[13px] leading-relaxed break-words">
        {{ item.detail }}
      </p>
    </div>

    <div class="text-muted mt-2 flex items-start gap-2 text-xs">
      <UIcon :name="item.noteIcon" class="mt-0.5 size-3.5 shrink-0" />
      <span>{{ item.note }}</span>
    </div>

    <PolicyStudioBackWithYouReply
      v-if="item.thread && canWrite && replying"
      class="mt-2.5"
      :item-id="item.thread.itemId"
      :policy-id="item.id"
      :thread-id="item.thread.id"
      @sent="replying = false"
    />

    <div class="mt-2.5 flex flex-wrap gap-2">
      <UButton
        v-if="item.thread && canWrite"
        :aria-expanded="replying"
        color="neutral"
        :icon="replying ? 'i-ph-x' : 'i-ph-arrow-bend-up-left'"
        :label="replying ? 'Cancel' : 'Reply'"
        size="xs"
        :variant="replying ? 'ghost' : 'soft'"
        @click="replying = !replying"
      />
      <UButton
        :to="item.to"
        :label="item.action"
        color="neutral"
        size="xs"
        variant="outline"
      />
    </div>
  </UCard>
</template>
